import React, { useEffect, useState, useCallback } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { useCookies } from 'react-cookie'
import { getZaloOtpSetting, updateZaloOtpSetting, fetchZaloOtpAccessToken } from '../../../controller/api/setting'

const DEFAULT_CONFIG = {
  accessToken: '',
  templateId: '',
  mode: 'development',
  otpField: 'otp',
  trackingPrefix: 'otp',
  appId: '',
  refreshToken: '',
  accessTokenExpiresAt: '',
  authorizationCode: '',
  codeVerifier: '',
}

const ZaloOtpSettings = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fetchingToken, setFetchingToken] = useState(false)

  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const hydrateConfig = useCallback((incoming) => {
    const next = {
      ...DEFAULT_CONFIG,
      ...(incoming || {}),
    }
    setConfig(next)
  }, [])

  const fetchSetting = useCallback(async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }
    setLoading(true)
    try {
      const data = await getZaloOtpSetting(token)
      hydrateConfig(data)
    } catch (error) {
      console.error('Fetch Zalo OTP setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể tải cấu hình OTP Zalo. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }, [hydrateConfig, token])

  useEffect(() => {
    fetchSetting()
  }, [fetchSetting])

  const handleInputChange = (field) => (event) => {
    setConfig((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }

    setSaving(true)
    try {
      const payload = {
        accessToken: config.accessToken,
        templateId: config.templateId,
        mode: config.mode,
        otpField: config.otpField,
        trackingPrefix: config.trackingPrefix,
        appId: config.appId,
        refreshToken: config.refreshToken,
        accessTokenExpiresAt: config.accessTokenExpiresAt,
        authorizationCode: config.authorizationCode,
        codeVerifier: config.codeVerifier,
      }

      const updated = await updateZaloOtpSetting(token, payload)
      hydrateConfig(updated)
      toast.success('Đã lưu cấu hình OTP Zalo thành công!')
    } catch (error) {
      console.error('Update Zalo OTP setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể lưu cấu hình OTP Zalo. Vui lòng thử lại.')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleFetchAccessToken = async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }

    if (!config.appId) {
      toast.warn('Vui lòng nhập app ID trước khi lấy token')
      return
    }

    const refreshToken = config.refreshToken.trim()
    const authorizationCode = config.authorizationCode.trim()
    const codeVerifier = config.codeVerifier.trim()
    const useRefreshToken = Boolean(refreshToken)
    if (!useRefreshToken && (!authorizationCode || !codeVerifier)) {
      toast.warn('Cần refresh token hoặc cặp authorization code và code verifier')
      return
    }

    setFetchingToken(true)
    try {
      const payload = useRefreshToken
        ? {
            appId: config.appId,
            grantType: 'refresh_token',
            refreshToken,
          }
        : {
            appId: config.appId,
            grantType: 'authorization_code',
            code: authorizationCode,
            codeVerifier,
          }

      const response = await fetchZaloOtpAccessToken(token, payload)
      if (response?.status !== 1 || !response?.config) {
        throw new Error(response?.message || 'Không thể lấy access token')
      }

      hydrateConfig(response.config)
      toast.success(
        useRefreshToken
          ? 'Đã làm mới access token Zalo thành công!'
          : 'Đã lấy access token Zalo thành công!'
      )
    } catch (error) {
      console.error('Fetch Zalo access token error', error)
      const message = error.response?.data?.message || error.message || 'Không thể lấy access token Zalo.'
      toast.error(message)
    } finally {
      setFetchingToken(false)
    }
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">⚙️ Cấu hình OTP qua Zalo</h4>
                <small className="text-muted">
                  Thiết lập thông tin gửi OTP bằng API Zalo Official Account
                </small>
                {config.appId && (
                  <div className="mt-2">
                    <small className="text-info">
                      📖 <a 
                        href={`https://developers.zalo.me/tools/explorer/${config.appId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        Hướng dẫn API Zalo cho App ID: {config.appId}
                      </a>
                    </small>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={fetchSetting} disabled={loading || saving || fetchingToken}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Tải lại'}
                </Button>
                <Button variant="outline-primary" size="sm" onClick={handleFetchAccessToken} disabled={loading || saving || fetchingToken}>
                  {fetchingToken ? <Spinner animation="border" size="sm" /> : 'Lấy token'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="zaloAccessToken">
                <Form.Label>Access Token</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nhập access token"
                  value={config.accessToken}
                  onChange={handleInputChange('accessToken')}
                  disabled={loading || saving}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="zaloRefreshToken">
                <Form.Label>Refresh Token</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nhập refresh token"
                  value={config.refreshToken}
                  onChange={handleInputChange('refreshToken')}
                  disabled={loading || saving || fetchingToken}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="zaloTemplateId">
                    <Form.Label>Template ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Nhập template ID"
                        value={config.templateId}
                        onChange={handleInputChange('templateId')}
                        disabled={loading || saving}
                      />
                    </Form.Group>
                  </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="zaloMode">
                    <Form.Label>Mode</Form.Label>
                    <Form.Control
                      type="text"
                        placeholder="development/production"
                        value={config.mode}
                        onChange={handleInputChange('mode')}
                        disabled={loading || saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="zaloOtpField">
                      <Form.Label>Tên trường OTP</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="otp"
                        value={config.otpField}
                        onChange={handleInputChange('otpField')}
                        disabled={loading || saving}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="zaloAppId">
                      <Form.Label>App ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Nhập app ID"
                        value={config.appId}
                        onChange={handleInputChange('appId')}
                        disabled={loading || saving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="zaloTokenExpiresAt">
                      <Form.Label>Access Token Expired At</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="ISO datetime"
                        value={config.accessTokenExpiresAt}
                        onChange={handleInputChange('accessTokenExpiresAt')}
                        disabled={loading || saving || fetchingToken}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="zaloAuthorizationCode">
                      <Form.Label>Authorization Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="OAuth authorization code"
                        value={config.authorizationCode}
                        onChange={handleInputChange('authorizationCode')}
                        disabled={loading || saving || fetchingToken}
                      />
                      <Form.Text className="text-muted">
                        Chỉ cần nhập khi chưa có refresh token. Mỗi authorization code chỉ dùng được một lần.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="zaloCodeVerifier">
                      <Form.Label>Code Verifier</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="PKCE code verifier"
                        value={config.codeVerifier}
                        onChange={handleInputChange('codeVerifier')}
                        disabled={loading || saving || fetchingToken}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="zaloTrackingPrefix">
                  <Form.Label>Tracking Prefix</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="otp"
                    value={config.trackingPrefix}
                    onChange={handleInputChange('trackingPrefix')}
                    disabled={loading || saving}
                  />
                  <Form.Text className="text-muted">
                    Tiền tố dùng để sinh tracking id khi gửi Zalo (ví dụ: <code>otp</code>, <code>zalo</code>).
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : 'Lưu cấu hình'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default ZaloOtpSettings
